import React, { useEffect, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionTemplate,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Magnetic } from "@/components/fina/Magnetic";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/5102b00f-8bc4-4859-8124-35bac0e1cdac/images/1e6a82ba98b6f23aa350bfb43fdbe0796c58ce41b18e3471b0390b4a41d1da25.png";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Weld reveal 0 -> 100% across the full pinned region.
  const reveal = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const edgeStart = useTransform(reveal, (v) => Math.max(0, v - 2.5));
  const edgeEnd = useTransform(reveal, (v) => Math.min(100, v + 0.5));

  const weldMask = useMotionTemplate`linear-gradient(to bottom left, #000 ${edgeStart}%, rgba(0,0,0,0.15) ${edgeEnd}%, transparent ${edgeEnd}%)`;
  const glowMask = useMotionTemplate`linear-gradient(to bottom left, transparent ${edgeStart}%, rgba(255,255,255,0.8) ${reveal}%, transparent ${edgeEnd}%)`;

  // Subtle parallax zoom + cinematic finale (zoom-through on release)
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.18]);
  const finaleScale = useTransform(scrollYProgress, [0.85, 1], [1, 1.18]);
  const finaleBlur = useTransform(scrollYProgress, [0.85, 1], [0, 8]);
  const finaleFilter = useMotionTemplate`blur(${finaleBlur}px)`;

  // Headline subtle scroll reactivity (compresses + lifts)
  const headlineY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const headlineScale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);
  const headlineLetterSpacing = useTransform(
    scrollYProgress,
    [0, 1],
    ["-0.04em", "-0.05em"]
  );

  // Mouse parallax — small camera move
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const sx = useSpring(mouseX, { stiffness: 60, damping: 18 });
  const sy = useSpring(mouseY, { stiffness: 60, damping: 18 });

  const bgX = useTransform(sx, [-1, 1], ["-1.2%", "1.2%"]);
  const bgY = useTransform(sy, [-1, 1], ["-1.2%", "1.2%"]);
  const midX = useTransform(sx, [-1, 1], ["-2.4%", "2.4%"]);
  const midY = useTransform(sy, [-1, 1], ["-2.4%", "2.4%"]);
  const fgX = useTransform(sx, [-1, 1], ["-4%", "4%"]);
  const fgY = useTransform(sy, [-1, 1], ["-4%", "4%"]);

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (e.clientY / window.innerHeight) * 2 - 1;
      mouseX.set(x);
      mouseY.set(y);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mouseX, mouseY]);

  const pct = useTransform(reveal, (v) => `${Math.round(v)}%`);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative w-full bg-[#0a0a0c]"
      style={{ height: "110vh" }}
    >
      <div className="sticky top-0 h-[100svh] w-full overflow-hidden">
        {/* Cinematic background gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 70% 30%, rgba(80,40,20,0.55) 0%, rgba(10,10,12,1) 60%), linear-gradient(180deg, #07070a 0%, #0a0a0c 100%)",
          }}
        />

        {/* Background — desaturated weld plate (deep) */}
        <motion.div
          style={{ scale, x: bgX, y: bgY, filter: finaleFilter }}
          className="absolute inset-0 will-change-transform brushed-steel"
        >
          <img
            src={HERO}
            alt=""
            className="w-full h-full object-cover"
            style={{
              filter: "grayscale(0.95) brightness(0.32) contrast(1.18)",
            }}
          />
        </motion.div>

        {/* Midground — the weld reveal layer with mask */}
        <motion.div
          style={{
            scale: finaleScale,
            x: midX,
            y: midY,
            WebkitMaskImage: weldMask,
            maskImage: weldMask,
            WebkitMaskSize: "100% 100%",
            maskSize: "100% 100%",
            filter: finaleFilter,
          }}
          className="absolute inset-0 will-change-[mask-image,transform]"
        >
          <img
            src={HERO}
            alt="Macro TIG weld"
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Cinematic light sweep across the metal */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none overflow-hidden mix-blend-screen"
        >
          <span
            className="absolute -top-1/3 left-0 h-[160%] w-[40%]"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 45%, rgba(255,180,120,0.18) 50%, rgba(255,255,255,0.08) 55%, transparent 100%)",
              animation: "lightsweep 9s ease-in-out infinite",
              animationDelay: "1.5s",
            }}
          />
        </div>

        {/* Molten leading-edge glow */}
        <motion.div
          style={{
            scale,
            x: midX,
            y: midY,
            WebkitMaskImage: glowMask,
            maskImage: glowMask,
            WebkitMaskSize: "100% 100%",
            maskSize: "100% 100%",
          }}
          className="absolute inset-0 pointer-events-none mix-blend-screen"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 75% 25%, rgba(255,140,60,0.65) 0%, rgba(255,87,34,0.3) 35%, transparent 70%)",
            }}
          />
        </motion.div>

        {/* Foreground dust motes / micro particles */}
        <motion.div
          style={{ x: fgX, y: fgY }}
          aria-hidden
          className="absolute inset-0 pointer-events-none"
        >
          {Array.from({ length: 28 }).map((_, i) => {
            const left = (i * 73) % 100;
            const top = (i * 41) % 100;
            const size = 1 + (i % 3);
            const dur = 8 + (i % 5);
            return (
              <span
                key={i}
                className="absolute rounded-full bg-white/40"
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  width: size,
                  height: size,
                  animation: `floatmote ${dur}s ease-in-out ${i * 0.2}s infinite alternate`,
                }}
              />
            );
          })}
        </motion.div>

        {/* CAD bracket marks — corner overlays */}
        <span className="absolute top-24 left-6 md:left-12 text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono">
          F-001 · macro · weld
        </span>
        <span className="absolute top-24 right-6 md:right-12 text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono">
          304 / 316L
        </span>

        {/* Cinematic vignette + grade */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/95 pointer-events-none" />
        <div className="absolute inset-0 vignette pointer-events-none" />

        {/* Copy */}
        <motion.div
          style={{ y: headlineY, scale: headlineScale }}
          className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-32 px-6 md:px-12 max-w-7xl mx-auto"
        >
          <p className="overline mb-6" data-testid="hero-overline">
            Fina Engineering &nbsp;/&nbsp; Est. 2015 &nbsp;/&nbsp; United Kingdom
          </p>

          <motion.h1
            style={{ letterSpacing: headlineLetterSpacing }}
            className="font-heading font-medium text-white text-5xl sm:text-7xl lg:text-[9rem] leading-[0.95]"
            data-testid="hero-headline"
          >
            Precision,
            <br />
            <span className="text-white/80">welded to</span>{" "}
            <span className="italic font-normal text-[#FF5722]">purpose.</span>
          </motion.h1>

          <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <p
              className="font-body text-white/70 max-w-md text-base md:text-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              A decade of precision stainless-steel fabrication for the
              industries that feed, heal and hydrate the nation.
            </p>

            <div className="flex items-center gap-5">
              <Magnetic strength={0.25}>
                <a
                  href="#capabilities"
                  data-testid="hero-cta-explore"
                  className="cta-glow inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-black font-medium text-sm hover:bg-white/90 transition"
                >
                  Explore our craft
                  <ArrowDown size={16} />
                </a>
              </Magnetic>
              <Magnetic strength={0.2}>
                <a
                  href="#contact"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center h-12 px-6 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition"
                >
                  Start a project
                </a>
              </Magnetic>
            </div>
          </div>
        </motion.div>

        {/* Weld-progress readout */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase text-white/70 flex flex-col items-center gap-2 z-10">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF5722] animate-pulse" />
            Weld&nbsp;
            <motion.span className="font-mono text-[#FF5722]">{pct}</motion.span>
          </span>
          <span className="w-[1px] h-8 bg-white/30 relative overflow-hidden">
            <span className="absolute top-0 left-0 w-full h-3 bg-white animate-[scrolldot_2s_infinite]" />
          </span>
        </div>
      </div>

      <style>{`
        @keyframes scrolldot { 0% { transform: translateY(-100%); } 100% { transform: translateY(300%); } }
        @keyframes floatmote {
          0% { transform: translate3d(0,0,0); opacity: 0.2; }
          50% { opacity: 0.7; }
          100% { transform: translate3d(0,-18px,0); opacity: 0.2; }
        }
      `}</style>
    </section>
  );
};
